import React, { useMemo, useState } from "react";
import { useLocation, useNavigate, useParams } from "react-router-dom";
import { TbCheck } from "react-icons/tb";
import { CustomButton, CustomModal } from "../../../components";
import AxiosInstance, { TokenService } from "../../../services/Api";
import styles from "./PlansPricing.module.css";

const LIST_PATH = "/dashboard/masters/plans-pricing";
const LIST_PLAN_API = "/saas/planList";
const CREATE_PLAN_API = "/saas/createPlan";

const FEATURES_TYPE_CODES = { core: 0, ai_core: 1 };

const isSuccess = (response) => response?.data?.status === true;

const getUserId = () => {
  const user = TokenService.getUser();
  return user?.id || user?.user_id || 1;
};

const formatPrice = (value) =>
  `₹${Number(value || 0).toLocaleString("en-IN")}`;

// ── UI helpers ────────────────────────────────────────────────────────────────
const RadioDot = ({ selected }) => (
  <span
    style={{
      width: "20px",
      height: "20px",
      borderRadius: "50%",
      border: selected ? "6px solid #F5821F" : "2px solid #9CA3AF",
      display: "inline-block",
      flexShrink: 0,
      boxSizing: "border-box",
      background: "#fff",
    }}
  />
);

const WarningIcon = () => (
  <svg width="70" height="70" viewBox="0 0 70 70" fill="none" xmlns="http://www.w3.org/2000/svg">
    <circle cx="35" cy="35" r="32" stroke="#F5821F" strokeWidth="2.5" fill="none" />
    <line x1="35" y1="20" x2="35" y2="41" stroke="#F5821F" strokeWidth="3.5" strokeLinecap="round" />
    <circle cx="35" cy="50" r="3" fill="#F5821F" />
  </svg>
);
// ─────────────────────────────────────────────────────────────────────────────

const PreviewPlansPricing = () => {
  const navigate = useNavigate();
  const location = useLocation();
  const { id } = useParams();
  const isEditMode = Boolean(id) && id !== "new";

  const {
    formData,
    ranges = [],
    slabId = null,
    planFeatures,
  } = location.state || {};

  // ── local state ───────────────────────────────────────────────────────────
  const [checkingPlans, setCheckingPlans] = useState(false);
  const [submitting, setSubmitting] = useState(false);
  const [archiveModalOpen, setArchiveModalOpen] = useState(false);
  const [activePlanRows, setActivePlanRows] = useState([]);
  const [selectedArchiveId, setSelectedArchiveId] = useState(null);
  const [statusModal, setStatusModal] = useState({
    open: false, type: "success", message: "",
  });

  // ── features list — always use live API data from navigation state ──────────
  // planFeatures = response from /saas/module/list passed from EditPlansPricing
  // ✅ No static PLAN_FEATURES fallback — show exactly what the API returned
  const filteredFeatures = useMemo(
    () => planFeatures || [],
    [planFeatures],
  );

  const moduleIds = useMemo(
    () => (planFeatures?.length ? planFeatures.map((m) => m.id) : []),
    [planFeatures],
  );

  const firstRange = ranges[0];
  const startingPrice = firstRange
    ? Number(formData?.planPricing?.[firstRange.key] || 0)
    : 0;
  const startingUsers = firstRange?.upTo ?? 0;

  if (!formData) {
    return (
      <div style={{ padding: "32px" }}>
        <p style={{ color: "#E23B3B" }}>No preview data found.</p>
        <CustomButton variant="outline-dark" fullWidth={false}
          onClick={() => navigate(LIST_PATH)}>
          Back to List
        </CustomButton>
      </div>
    );
  }

  // ── build create payload ──────────────────────────────────────────────────
  const buildPayload = (deactivatePlanId = null) => ({
    plan_name: formData.planName,
    module_id: moduleIds,
    plan_description: formData.planDescription,
    public_badge: formData.publicBadge?.value || "",
    custom_badge_text: formData.customBadgeText || "",
    highlight_plan: formData.highlightOnPublicPricing ? 1 : 0,
    features_type: FEATURES_TYPE_CODES[formData.featuresType?.value] ?? 0,
    deactivate_plan_id: deactivatePlanId,
    created_by: getUserId(),
    slab_pricing: ranges.map((range, index) => ({
      slab_id: slabId,
      range_no: index + 1,
      max_users: Number(range.upTo),
      plan_price: Number(formData.planPricing?.[range.key] || 0),
      amc_price: Number(formData.amcPricing?.[range.key] || 0),
    })),
  });

  // ── perform create ────────────────────────────────────────────────────────
  const performCreate = async (deactivatePlanId = null) => {
    setSubmitting(true);
    try {
      const response = await AxiosInstance.post(
        CREATE_PLAN_API,
        buildPayload(deactivatePlanId),
      );
      if (isSuccess(response)) {
        setArchiveModalOpen(false);
        setStatusModal({
          open: true, type: "success",
          message: response.data?.message || "Plan created successfully",
        });
      } else {
        throw new Error(response?.data?.message || "Failed to create plan");
      }
    } catch (err) {
      setArchiveModalOpen(false);
      setStatusModal({
        open: true, type: "error",
        message: err?.response?.data?.message || err?.message || "Error creating plan",
      });
    } finally {
      setSubmitting(false);
    }
  };

  // ── "Create Plan" click ───────────────────────────────────────────────────
  // ✅ Fetch ALL plans with limit:100 — never use paginated data to count actives
  // Rule: activePlans.length >= 2 → show archive modal
  //       activePlans.length  < 2 → create directly
  const handleCreatePlanClick = async () => {
    setCheckingPlans(true);
    try {
      const response = await AxiosInstance.post(LIST_PLAN_API, {
        page: 1, limit: 100, status: "", // ✅ all plans, not paginated
      });

      const allPlans = response.data?.status ? (response.data.data || []) : [];
      const activePlans = allPlans.filter((p) => p.status === "Active");

      if (activePlans.length >= 2) {
        // Build modal rows from each active plan's own range data
        const rows = activePlans.map((p) => ({
          id: p.plan_id,
          planName: p.plan_name,
          planType: p.plan_type,
          // ✅ Use each plan's own range1..range4 (not the new plan's ranges)
          ranges: ["range1", "range2", "range3", "range4"]
            .filter((key) => p[key] != null)
            .map((key, i) => ({
              label: `Range ${i + 1}`,
              price: p[key].plan_price,
              maxUsers: p[key].max_users,
            })),
        }));

        setActivePlanRows(rows);
        setSelectedArchiveId(rows[0]?.id ?? null); // pre-select first row
        setArchiveModalOpen(true);
      } else {
        // ✅ 0 or 1 active plan → create directly, no modal
        performCreate(null);
      }
    } catch (err) {
      // On fetch error, proceed — backend will validate
      performCreate(null);
    } finally {
      setCheckingPlans(false);
    }
  };

  const handleArchiveAndCreate = () => {
    if (!selectedArchiveId) return;
    performCreate(selectedArchiveId);
  };

  const handleStatusOk = () => {
    const wasSuccess = statusModal.type === "success";
    setStatusModal((prev) => ({ ...prev, open: false }));
    if (wasSuccess) navigate(LIST_PATH);
  };

  const isProcessing = checkingPlans || submitting;

  return (
    <div>
      {/* Breadcrumb */}
      {/* <div className={styles.breadcrumb}>
        <span className={styles.breadcrumbLink} onClick={() => navigate(LIST_PATH)}>
          Plans &amp; Pricing
        </span>
        <span className={styles.breadcrumbSeparator}>&gt;</span>
        <span className={styles.breadcrumbCurrent}>
          {isEditMode ? "Edit Plan" : "Create Plan"}
        </span>
      </div> */}

      <div className={styles.pageHeader}>
        <div>
          <h1 className={styles.pageTitle}>
            {isEditMode ? "Edit Plan" : "Create Plan"}
          </h1>
        </div>
      </div>

      {/* Preview Card */}
      <div className={styles.card}>
        <h2 className={styles.sectionTitle}>Preview</h2>
        <div style={{ display: "flex", justifyContent: "center", padding: "24px 0" }}>
          <div style={{
            width: "340px",
            maxWidth: "340px",
            border: "1.5px solid #E5E7EB",
            borderRadius: "16px",
            padding: "28px 24px",
            fontFamily: "inherit",
            boxShadow: "0 2px 12px rgba(0,0,0,0.06)",
          }}>
            {/* Plan name + badge */}
            {/* Plan name + badge */}
            <div style={{
              display: "flex",
              justifyContent: "space-between",
              alignItems: "flex-start",
              gap: "10px",
              marginBottom: "12px",
            }}>
              <span style={{
                fontSize: "20px",
                fontWeight: 700,
                color: "#1D4ED8",
                flex: "1 1 auto",
                minWidth: 0,               // ✅ allows flex item to shrink below content width
                wordBreak: "break-word",   // ✅ break long words like "rrrrrr..."
                overflowWrap: "anywhere",  // ✅ modern fallback for un-spaced strings
                lineHeight: 1.3,
              }}>
                {formData.planName || "Plan Name"}
              </span>
              {(formData.publicBadge?.value === "Others"
                ? formData.customBadgeText
                : formData.publicBadge?.label) && (
                  <span style={{
                    background: "#CCFBF1",
                    color: "#0F766E",
                    fontSize: "12px",
                    fontWeight: 600,
                    padding: "4px 10px",
                    borderRadius: "20px",
                    whiteSpace: "nowrap",  // ✅ badge stays on one line
                    flexShrink: 0,         // ✅ badge never gets squeezed
                    alignSelf: "flex-start",
                  }}>
                    {formData.publicBadge?.value === "Others"
                      ? formData.customBadgeText
                      : formData.publicBadge?.label}
                  </span>
                )}
            </div>

            <div style={{ color: "#6B7280", fontSize: "13px", marginBottom: "4px" }}>Starting at</div>
            <div style={{ fontSize: "36px", fontWeight: 800, color: "#111827", lineHeight: 1 }}>
              {formatPrice(startingPrice)}
            </div>
            <div style={{ fontSize: "13px", color: "#6B7280", marginBottom: "20px" }}>
              / year &nbsp;·&nbsp; for up to {startingUsers.toLocaleString("en-IN")} users
            </div>

            <div style={{ fontWeight: 700, fontSize: "14px", color: "#111827", marginBottom: "10px" }}>
              Slab Pricing by Users
            </div>
            <div style={{ background: "#F9FAFB", borderRadius: "10px", overflow: "hidden", marginBottom: "20px" }}>
              {ranges.map((range, i) => (
                <div
                  key={range.key}
                  style={{
                    display: "flex",
                    justifyContent: "space-between",
                    alignItems: "center",
                    padding: "10px 14px",
                    borderBottom: i < ranges.length - 1 ? "1px solid #E5E7EB" : "none",
                  }}
                >
                  <span style={{ fontSize: "14px", color: "#374151" }}>
                    Up to {Number(range.upTo).toLocaleString("en-IN")} users
                  </span>
                  <span style={{ fontSize: "14px", fontWeight: 600, color: "#111827" }}>
                    {formatPrice(formData.planPricing?.[range.key])}/year
                  </span>
                </div>
              ))}
            </div>

            <div style={{ fontSize: "12px", fontWeight: 700, letterSpacing: "0.08em", color: "#6B7280", marginBottom: "8px" }}>
              INCLUDES
            </div>
            <ul style={{ margin: "0 0 20px", padding: 0, listStyle: "none" }}>
              {[
                `${filteredFeatures.length}+ ${formData.featuresType?.label || "Core Features"}`,
                "One Time Setup",
                "Product Training",
                "Unlimited Cloud Storage",
                "24/7 support for all issues",
              ].map((item) => (
                <li key={item} style={{ display: "flex", alignItems: "center", gap: "8px", marginBottom: "6px", fontSize: "14px" }}>
                  <TbCheck size={16} color="#22C55E" />
                  {item}
                </li>
              ))}
            </ul>

            {firstRange && (
              <div style={{ background: "#EFF6FF", borderRadius: "10px", padding: "14px 16px" }}>
                {/* ✅ AMC label */}
                <span style={{ fontSize: "12px", fontWeight: 400, color: "#1D4ED8", marginBottom: "4px" }}>
                  AMC Price around
                </span>
                {/* ✅ Bold large price — matches Image 1 */}
                <span style={{ paddingLeft: "3px", fontSize: "13px", fontWeight: 800, color: "#1D4ED8", marginBottom: "10px" }}>
                  {formatPrice(formData.amcPricing?.[firstRange.key])}
                </span>
                <div style={{ fontSize: "11px", fontWeight: 800, letterSpacing: "0.08em", color: "#6B7280", marginBottom: "6px" }}>
                  INCLUDES
                </div>
                <ul style={{ margin: 0, padding: 0, listStyle: "none" }}>
                  {["Cloud Support", "24/7 support for all issues"].map((item) => (
                    <li key={item} style={{ display: "flex", alignItems: "center", gap: "8px", marginBottom: "4px", fontSize: "13px" }}>
                      <TbCheck size={14} color="#22C55E" />
                      {item}
                    </li>
                  ))}
                </ul>
              </div>
            )}
          </div>
        </div>
      </div>

      {/* Plan Features */}
      <div className={styles.card}>
        <div className={styles.cardHeaderRow}>
          <h2 className={styles.sectionTitle} style={{ marginBottom: 0, paddingBottom: 0, borderBottom: "none" }}>
            Plan Features
          </h2>
          <span className={styles.sectionCount}>{filteredFeatures.length} Features</span>
        </div>
        <ul className={styles.checklist}>
          {filteredFeatures.map((feature) => (
            <li key={feature.id} className={styles.checklistItem}>
              <TbCheck size={16} className={styles.checklistIcon} />
              <span>
                <span className={styles.checklistName}>{feature.name}</span>
                {" - "}
                <span className={styles.checklistDesc}>{feature.description}</span>
              </span>
            </li>
          ))}
        </ul>
      </div>

      {/* Actions */}
      <div className={styles.formActions}>
        <CustomButton
          variant="outline-dark"
          fullWidth={false}
          disabled={isProcessing}
          onClick={() =>
            navigate(`${LIST_PATH}/create`, {
              state: {
                restoredFormData: formData,
                restoredSlabId: slabId,
                restoredRanges: ranges,  // ✅ preserve exact ranges labels
              },
            })
          }
        >
          Back
        </CustomButton>
        <CustomButton
          variant="primary"
          fullWidth={false}
          className={styles.btnOrange}
          loading={isProcessing}
          loadingText={checkingPlans ? "Checking plans..." : "Creating..."}
          onClick={handleCreatePlanClick}
        >
          {isEditMode ? "Update Plan" : "Create Plan"}
        </CustomButton>
      </div>

      {/* ══ Archive Selection Modal — only when activePlans.length >= 2 ══ */}
      <CustomModal
        isOpen={archiveModalOpen}
        onClose={() => !submitting && setArchiveModalOpen(false)}
        title=""
        size="lg"
        showCloseButton={false}
      >
        <div style={{ textAlign: "center", padding: "8px 0" }}>

          {/* Icon */}
          <div style={{ display: "flex", justifyContent: "center", marginBottom: "16px" }}>
            <WarningIcon />
          </div>

          {/* Title */}
          <h2 style={{ margin: "0 0 12px", fontSize: "26px", fontWeight: 700, color: "#111827" }}>
            Archive an Active Plan
          </h2>

          {/* Description */}
          <p style={{
            color: "#6B7280",
            margin: "0 auto 24px",
            maxWidth: "520px",
            fontSize: "14px",
            lineHeight: "1.6",
          }}>
            You already have{" "}
            <strong style={{ color: "#111827" }}>{activePlanRows.length} Active plans</strong>.
            Select one to archive before creating the new plan.
            Archived plans will no longer be visible to your users.
          </p>

          {/* Archive selection table */}
          <div style={{ textAlign: "left", overflowX: "auto", marginBottom: "28px" }}>
            <table style={{ width: "100%", borderCollapse: "collapse" }}>
              <thead>
                <tr style={{ background: "#F3F4F6", borderBottom: "2px solid #F5821F" }}>
                  <th style={{ padding: "12px 16px", fontWeight: 700, fontSize: "14px", color: "#111827", textAlign: "left" }}>
                    Select
                  </th>
                  <th style={{ padding: "12px 16px", fontWeight: 700, fontSize: "14px", color: "#111827", textAlign: "left" }}>
                    Plan Name
                  </th>
                  <th style={{ padding: "12px 16px", fontWeight: 700, fontSize: "14px", color: "#111827", textAlign: "left" }}>
                    Plan Type
                  </th>
                  {/* Dynamic range headers from first active plan */}
                  {(activePlanRows[0]?.ranges || []).map((_, i) => (
                    <th key={i} style={{ padding: "12px 16px", fontWeight: 700, fontSize: "14px", color: "#111827", textAlign: "left" }}>
                      Range {i + 1}
                    </th>
                  ))}
                </tr>
              </thead>
              <tbody>
                {activePlanRows.map((row) => {
                  const isSelected = selectedArchiveId === row.id;
                  return (
                    <tr
                      key={row.id}
                      onClick={() => setSelectedArchiveId(row.id)}
                      style={{
                        background: isSelected ? "#FFF7F0" : "#fff",
                        cursor: "pointer",
                        outline: isSelected ? "2px solid #F5821F" : "none",
                        outlineOffset: "-2px",
                        transition: "background 0.15s",
                        borderBottom: "1px solid #E5E7EB",
                      }}
                    >
                      <td style={{ padding: "14px 16px" }}>
                        <RadioDot selected={isSelected} />
                      </td>
                      <td style={{ padding: "14px 16px", fontWeight: 700, color: "#111827", fontSize: "14px" }}>
                        {row.planName}
                      </td>
                      <td style={{ padding: "14px 16px", color: "#374151", fontSize: "14px" }}>
                        {row.planType}
                      </td>
                      {/* ✅ Each plan's own range prices + max_users */}
                      {row.ranges.map((tier, i) => (
                        <td key={i} style={{ padding: "14px 16px" }}>
                          <div style={{ fontWeight: 700, color: "#111827", fontSize: "14px" }}>
                            {formatPrice(tier.price)}
                          </div>
                          <div style={{ color: "#6B7280", fontSize: "13px", marginTop: "2px" }}>
                            Up to {Number(tier.maxUsers).toLocaleString("en-IN")} users
                          </div>
                        </td>
                      ))}
                    </tr>
                  );
                })}
              </tbody>
            </table>
          </div>

          {/* Buttons */}
          <div style={{ display: "flex", justifyContent: "center", gap: "16px" }}>
            <CustomButton
              variant="outline-dark"
              fullWidth={false}
              disabled={submitting}
              onClick={() => setArchiveModalOpen(false)}
            >
              Cancel
            </CustomButton>
            <CustomButton
              variant="primary"
              fullWidth={false}
              className={styles.btnOrange}
              disabled={!selectedArchiveId || submitting}
              loading={submitting}
              loadingText="Processing..."
              onClick={handleArchiveAndCreate}
            >
              Archive &amp; Create New Plan
            </CustomButton>
          </div>
        </div>
      </CustomModal>

      {/* Success / Error modal */}
      <CustomModal
        isOpen={statusModal.open}
        onClose={handleStatusOk}
        title={statusModal.type === "success" ? "Success" : "Failed"}
        size="sm"
      >
        <p className={styles.confirmText}>{statusModal.message}</p>
        <div className={styles.confirmActions}>
          <CustomButton
            variant="primary"
            fullWidth={false}
            className={styles.btnOrange}
            onClick={handleStatusOk}
          >
            OK
          </CustomButton>
        </div>
      </CustomModal>
    </div>
  );
};

export default PreviewPlansPricing;
